import { DataGridPremium, DataGridPremiumProps, GRID_AGGREGATION_FUNCTIONS, GridColumnMenuProps, GridColumnsPanelProps, GridLocaleText } from "@mui/x-data-grid-premium";
import React, { useCallback, useMemo, useState, useEffect } from "react";
import { CSSProperties } from "react";
import { defaultTableDensity, tableSettingsProps } from "src/core/tables/useTableSettings";
import { CustomColumnsPanel } from "./helpers/CustomColumnsPanel";
import { useAggregationModel } from "src/core/tables/helpers/useAggregationModel";
import { CustomToolbar } from "./helpers/CustomToolbar";
import { CustomColumnMenu } from "./helpers/CustomColumnMenu";
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { TableDialog } from "./helpers/TableDialog";

export const CustomDataGrid = ({ wrapperStyle, tableSettings, ...dataGridProps }: CustomDataGridProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const defaultWrapperStyle = { height: isMobile ? "max(84.5vh, 400px)" : "max(calc(84.5vh + 28px), 400px)", width: '100%' };
  const [aggregationModel, setAggregationModel] = useAggregationModel(tableSettings, dataGridProps?.columns);
  const handleAggregationModelChange = newModel => setAggregationModel(newModel);

  const customizedAggregationFunctions = Object.keys(GRID_AGGREGATION_FUNCTIONS).reduce((acc, functionName) => {
    const originalFunction = GRID_AGGREGATION_FUNCTIONS[functionName];
    acc[functionName] = {
      ...originalFunction,
      valueFormatter: (params) => {
        const v = params?.value;
        const isEmpty =
          v == null ||
          (typeof v === 'number' && !Number.isFinite(v)) ||
          (typeof v === 'number' && Number.isNaN(v));
        if (functionName !== 'size' && isEmpty) return '';
        let formatted = v;
        if (functionName === 'sum' && typeof v === 'number') {
          formatted = new Intl.NumberFormat('en-US', { style: 'decimal', minimumFractionDigits: 0, maximumFractionDigits: 2, }).format(v);
        } else if (originalFunction.valueFormatter) {
          formatted = originalFunction.valueFormatter(params);
        }
        if (functionName === 'size') {
          return `${formatted} Total Rows`;
        }
        return `${functionName.charAt(0).toUpperCase() + functionName.slice(1)}: ${formatted}`;
      },
    };
    return acc;
  }, {} as any);

  const CustomColumnsPanelWrapper = (props: GridColumnsPanelProps & { tableSettings?: any }) => {
    const { tableSettings, ...rest } = props;
    return <CustomColumnsPanel {...rest} tableSettings={tableSettings} />;
  };

  const enhancedColumns = useEnhancedColumns(dataGridProps.columns ?? []);

  // detail panel provider: direct prop takes precedence; fallback to tableSettings
  const resolvedGetDetailPanelContent = (dataGridProps as any).getDetailPanelContent ?? tableSettings?.getDetailPanelContent;

  const mergedLocaleText = useMemo<Partial<GridLocaleText>>(() => {
    if (tableSettings?.status === 401) {
      return { ...myLocaleText, noRowsLabel: 'You do not have the authorization to see this, talk to an Admin if this need to change', };
    }
    return myLocaleText;
  }, [tableSettings?.status]);

  const effectiveLoading = useMemo(() => { return tableSettings?.status === 401 ? false : (tableSettings?.loading ?? false); }, [tableSettings?.status, tableSettings?.loading]);

  // NEW: detail panel expansion (default collapsed unless enabled)
  const getRowIdForExpansion = useCallback((row: any) => {
    if (tableSettings?.getRowId) return tableSettings.getRowId(row);
    return row?.id ?? row?.Id;
  }, [tableSettings?.getRowId]);

  const shouldDefaultExpandDetailPanels = Boolean(tableSettings?.detailPanelExpandedByDefault) && Boolean(resolvedGetDetailPanelContent);

  const computedDefaultExpandedRowIds = useMemo(() => {
    if (!shouldDefaultExpandDetailPanels) return [];
    const rows = tableSettings?.allRows ?? [];
    return rows.map(getRowIdForExpansion).filter((id) => id != null);
  }, [shouldDefaultExpandDetailPanels, tableSettings?.allRows, getRowIdForExpansion]);

  const [detailPanelExpandedRowIds, setDetailPanelExpandedRowIds] = useState<any[]>(computedDefaultExpandedRowIds);

  // keep in sync if rows change (and default-expand is enabled)
  useEffect(() => {
    if (!shouldDefaultExpandDetailPanels) return;
    setDetailPanelExpandedRowIds(computedDefaultExpandedRowIds);
  }, [shouldDefaultExpandDetailPanels, computedDefaultExpandedRowIds]);

  const gridRows = tableSettings?.allRows ?? [];
  const autoHeightRowLimit = 20;
  const shouldUseAutoHeight = tableSettings?.autoHeight === true && gridRows.length <= autoHeightRowLimit;

  return (
    <div style={tableSettings?.customWrapperStyle == null ? (shouldUseAutoHeight ? { height: "auto", width: '100%' } : wrapperStyle ?? defaultWrapperStyle) : tableSettings?.customWrapperStyle}>
      <DataGridPremium headerFilters={tableSettings?.hasToolbar ? tableSettings?.headerFilter ?? true : false} localeText={mergedLocaleText} aggregationModel={tableSettings?.hasToolbar ? aggregationModel : {}} aggregationFunctions={{ ...customizedAggregationFunctions, }} onAggregationModelChange={handleAggregationModelChange} rows={gridRows} treeData={tableSettings?.treeData ?? false} rowReordering={false} rowModesModel={tableSettings.rowModes} onRowModesModelChange={tableSettings.setRowModes} editMode={tableSettings?.editMode ?? "row"} slots={{ toolbar: (tableSettings?.hasToolbar ?? false) ? () => CustomToolbar(tableSettings) : null, columnsPanel: (panelProps) => (<CustomColumnsPanel {...panelProps} tableSettings={tableSettings} />), columnMenu: (props) => (<CustomColumnMenu props={props} tableSettings={tableSettings} columns={enhancedColumns} />), }} slotProps={{ toolbar: { showQuickFilter: false }, }} rowHeight={tableSettings?.density ?? defaultTableDensity} apiRef={tableSettings?.apiRef} pinnedRows={tableSettings?.pinnedRows ?? []} columnVisibilityModel={tableSettings.columnVisibilityModel} onColumnVisibilityModelChange={tableSettings.setColumnVisibilityModel} rowSelectionModel={tableSettings?.rowSelectionModel} onRowSelectionModelChange={tableSettings?.handleRowSelectionModelChange ?? tableSettings?.setRowSelectionModel} pinnedColumns={{}} className={tableSettings?.treeData ? 'tree' : ''} onPinnedColumnsChange={tableSettings?.handlePinnedColumnsChange} hideFooterRowCount={tableSettings?.hideFooterRowCount ?? true} hideFooter={tableSettings?.hideFooter ?? true} getTreeDataPath={useCallback((row: any) => row.path, [])} checkboxSelection={tableSettings?.checkboxSelection ?? false} isRowSelectable={tableSettings?.isRowSelectable} isGroupExpandedByDefault={tableSettings?.isGroupExpandedByDefault} loading={effectiveLoading} disableRowSelectionOnClick={tableSettings?.disableSelectionOnClick ?? true} groupingColDef={tableSettings?.groupingColDef ?? undefined} getDetailPanelHeight={() => 'auto'} getDetailPanelContent={resolvedGetDetailPanelContent} /* NEW: controlled expansion state */ detailPanelExpandedRowIds={detailPanelExpandedRowIds} onDetailPanelExpandedRowIdsChange={(newIds) => setDetailPanelExpandedRowIds(newIds as any[])} getRowId={tableSettings?.getRowId} processRowUpdate={(newRow, oldRow) => tableSettings?.processRowUpdateRef?.current?.(newRow, oldRow) ?? newRow} filterModel={tableSettings?.filterModel ?? { items: [], quickFilterValues: [tableSettings?.quickFilterValues ?? []] }} onFilterModelChange={(newModel) => { tableSettings?.setFilterModel(newModel); }} initialState={{ filter: { filterModel: { items: [tableSettings?.initialFilter ?? { field: "", operator: "", value: "" }] } }, sorting: { sortModel: tableSettings?.initalSorting ?? null }, }} getRowClassName={tableSettings?.getRowClassName} {...dataGridProps} autoHeight={shouldUseAutoHeight} columns={enhancedColumns} />
      <TableDialog ref={tableSettings.tableDialog.ref} />
    </div>
  );
};

export const useEnhancedColumns = (columns) => {
  const theme = useTheme();
  return useMemo(() => {
    return columns.map((col) => {
      if (col.type === 'boolean' && !col.renderCell) {
        return {
          ...col,
          align: 'center',
          renderCell: (params) => {
            if (typeof params.value !== 'boolean') {
              return "";
            }
            return Boolean(params.value) ? (
              <CheckIcon sx={{ color: theme.palette.success.main }} />
            ) : (
              <CloseIcon sx={{ color: theme.palette.error.main }} />
            );
          },
        };
      }
      return col;
    });
  }, [columns, theme]);
};

declare module '@mui/x-data-grid' {
  interface ColumnsPanelPropsOverrides {
    tableSettings: any;
  }
}

declare module '@mui/x-data-grid' {
  interface columnMenuPropsOverrides {
    props: GridColumnMenuProps;
    tableSettings: any;
  }
}

declare module '@mui/x-data-grid' {
  interface ToolbarPropsOverrides {
    tableSettings: any;
  }
}

const myLocaleText = { filterOperatorImageIdFilter: 'Filter by Image ID', filterOperatorThumbnailIdFilter: 'Filter by Thumbnail ID', } as Partial<GridLocaleText>;

export interface CustomDataGridProps extends Omit<DataGridPremiumProps<any>, 'rows'> {
  wrapperStyle?: CSSProperties,
  tableSettings?: tableSettingsProps,
  rows?: DataGridPremiumProps<any>['rows']
}
